import { useEffect, useMemo, useRef, useState } from "react";
import PublicAPIService from "../../Service/PublicAPIService";
import styles from "./OpenOrderForm.module.css";
import TurnstileWidget from "./TurnstileWidget";
import {
  buildOpenOrderStatusPath,
  storeOpenOrderAccessToken,
} from "./openOrderAccessToken";

const EMPTY_CONTACT = {
  firstName: "",
  lastName: "",
  businessName: "",
  phone: "",
  email: "",
};

const EMPTY_ADDRESS = {
  address1: "",
  address2: "",
  city: "",
  state: "PA",
  zip: "",
};

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const FIELD_LABELS = {
  "contact.firstName": "First Name",
  "contact.lastName": "Last Name",
  "contact.businessName": "Clinic / Business Name",
  "contact.phone": "Phone",
  "contact.email": "Email",
  "shippingAddress.address1": "Shipping Address Line 1",
  "shippingAddress.city": "Shipping City",
  "shippingAddress.state": "Shipping State",
  "shippingAddress.zip": "Shipping ZIP Code",
  "billingAddress.address1": "Billing Address Line 1",
  "billingAddress.city": "Billing City",
  "billingAddress.state": "Billing State",
  "billingAddress.zip": "Billing ZIP Code",
  products: "Product quantity",
  "shipping.optionCode": "Shipping Preference",
  "shipping.mode": "Shipping Preference",
  turnstileToken: "Security verification",
};

const PHONE_PATTERN = /^[0-9()+.\-\s]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/;
const STATE_PATTERN = /^[A-Za-z]{2}$/;

const FEDEX_RATE_DEBOUNCE_MS = 500;

const formatValidationMessage = (fields = []) => {
  const labels = Array.from(
    new Set(fields.map((field) => FIELD_LABELS[field]).filter(Boolean)),
  );
  if (!labels.length) return "Review the highlighted information and try again.";
  return `Review the highlighted information and try again: ${labels.join(", ")}.`;
};

export const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value ?? 0));

export const calculateEstimatedSubtotal = (lines) =>
  Number(
    lines
      .reduce(
        (total, line) =>
          total + Number(line.standardPrice ?? 0) * Number(line.quantity ?? 0),
        0,
      )
      .toFixed(2),
  );

const isStockManaged = (product) =>
  product?.manageStock === true ||
  product?.ManageStock === true ||
  product?.manageStock === 1 ||
  product?.ManageStock === 1 ||
  String(product?.manageStock ?? product?.ManageStock ?? "").toLowerCase() === "true";

const getStockQty = (product) => {
  const parsed = Number(product?.stockQty ?? product?.StockQty ?? 0);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const getStockStatusMessage = (product) => {
  if (!isStockManaged(product)) return "";
  const stockQty = getStockQty(product);
  if (stockQty === 0) return "Out of stock";
  if (stockQty <= 5) return `Low stock - Only ${stockQty} Qty remaining`;
  return "";
};

const hasSelectedMoreThanStock = (line) =>
  isStockManaged(line) && Number(line.quantity ?? 0) > getStockQty(line);

const formatStockShortageMessage = (shortages = []) => {
  if (!Array.isArray(shortages) || shortages.length === 0) {
    return "One or more selected products do not have enough available stock. Refresh the catalog and try again.";
  }
  return shortages
    .map((item) => {
      const label = item.ProductCode || item.ProductName || `Product ${item.ProductID}`;
      return `${label}: requested ${item.RequestedQty}, available ${item.AvailableQty}`;
    })
    .join(" ");
};

const hasValidFedexRateAddress = (address) =>
  Boolean(String(address?.address1 ?? "").trim()) &&
  Boolean(String(address?.city ?? "").trim()) &&
  STATE_PATTERN.test(String(address?.state ?? "").trim()) &&
  ZIP_PATTERN.test(String(address?.zip ?? "").trim());

const buildFedexRatesRequestKey = ({ productGroupId, products, shippingAddress }) =>
  JSON.stringify({
    productGroupId,
    products: products
      .map((line) => ({
        productId: line.productId,
        quantity: Number(line.quantity ?? 0),
      }))
      .filter((line) => line.productId && line.quantity > 0)
      .sort((left, right) => left.productId - right.productId),
    shippingAddress: {
      address1: String(shippingAddress.address1 ?? "").trim().toLowerCase(),
      address2: String(shippingAddress.address2 ?? "").trim().toLowerCase(),
      city: String(shippingAddress.city ?? "").trim().toLowerCase(),
      state: String(shippingAddress.state ?? "").trim().toUpperCase(),
      zip: String(shippingAddress.zip ?? "").trim(),
      countryCode: "US",
    },
  });

const mapFedexRatesByCode = (rates = []) =>
  rates.reduce((result, rate) => {
    if (rate?.code) result[rate.code] = rate;
    return result;
  }, {});

const InputField = ({
  label,
  name,
  value,
  onChange,
  required = false,
  optional = false,
  type = "text",
  placeholder = "",
  disabled = false,
  className = "",
  ariaLabel,
  invalid = false,
}) => (
  <label className={`${styles.field} ${className}`}>
    <span className={styles.fieldLabel}>
      <span>{label}</span>
      {required && <span aria-hidden="true" className={styles.requiredMark}>*</span>}
      {optional && <span className={styles.optionalText}> (optional)</span>}
    </span>
    <input
      aria-label={ariaLabel}
      aria-invalid={invalid ? "true" : undefined}
      disabled={disabled}
      name={name}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      type={type}
      value={value}
    />
  </label>
);

const StateField = ({
  label = "State",
  name = "state",
  value,
  onChange,
  required = false,
  disabled = false,
  invalid = false,
}) => (
  <label className={`${styles.field} ${styles.fieldState}`}>
    <span className={styles.fieldLabel}>
      <span>{label}</span>
      {required && <span aria-hidden="true" className={styles.requiredMark}>*</span>}
    </span>
    <select
      className={styles.stateSelect}
      aria-invalid={invalid ? "true" : undefined}
      disabled={disabled}
      name={name}
      onChange={onChange}
      required={required}
      value={value}
    >
      {US_STATES.map((state) => (
        <option key={state} value={state}>
          {state}
        </option>
      ))}
    </select>
  </label>
);

const AddressFields = ({ value, onChange, disabled = false, validationPrefix = "", invalidFields = new Set() }) => (
  <div className={styles.addressFields}>
    <InputField
      className={styles.fieldStandard}
      disabled={disabled}
      label="Address Line 1"
      name="address1"
      onChange={onChange}
      placeholder="Enter Address Line 1"
      required
      value={value.address1}
      invalid={invalidFields.has(`${validationPrefix}.address1`)}
    />
    <InputField
      className={styles.fieldStandard}
      disabled={disabled}
      label="Address Line 2"
      name="address2"
      onChange={onChange}
      optional
      placeholder="Enter Address Line 2"
      value={value.address2}
    />
    <InputField
      className={styles.fieldStandard}
      disabled={disabled}
      label="City"
      name="city"
      onChange={onChange}
      placeholder="Enter City"
      required
      value={value.city}
      invalid={invalidFields.has(`${validationPrefix}.city`)}
    />
    <StateField
      disabled={disabled}
      onChange={onChange}
      required
      value={value.state}
      invalid={invalidFields.has(`${validationPrefix}.state`)}
    />
    <InputField
      className={styles.fieldZip}
      disabled={disabled}
      label="ZIP Code"
      name="zip"
      onChange={onChange}
      placeholder="Enter Zip"
      required
      value={value.zip}
      invalid={invalidFields.has(`${validationPrefix}.zip`)}
    />
  </div>
);

const Step = ({
  number,
  title,
  summary,
  summaryContent,
  active,
  completed = false,
  onOpen,
  headerAction,
  children,
}) => (
  <section
    className={`${styles.step} ${active ? styles.stepActive : ""} ${
      completed && !active ? styles.stepCompleted : ""
    }`}
  >
    <div className={styles.stepHeader}>
      <button
        aria-expanded={active}
        className={styles.stepHeaderButton}
        onClick={onOpen}
        type="button"
      >
        <span className={styles.stepNumber}>{number}</span>
        <span className={styles.stepHeading}>
          <strong>{title}</strong>
          {summary && !summaryContent && <small>{summary}</small>}
        </span>
      </button>
      {active && headerAction && <div className={styles.stepHeaderAction}>{headerAction}</div>}
      {!active && summaryContent && <div className={styles.stepSummary}>{summaryContent}</div>}
    </div>
    {active && <div className={styles.stepContent}>{children}</div>}
  </section>
);

const PublicHeader = () => (
  <header className={styles.publicHeader}>
    <div className={styles.tvBrand}>
      <img className={styles.tvIcon} src="/tissue_vault_icon.svg" alt="" aria-hidden="true" />
      <img className={styles.tvLogo} src="/TissueVaultTextLogo.svg" alt="Tissue Vault" />
    </div>
    <img className={styles.companyLogo} src="/companyLogo.png" alt="Advanced Solution" />
  </header>
);

const OpenOrderForm = () => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [activeStep, setActiveStep] = useState(1);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [shippingAddress, setShippingAddress] = useState(EMPTY_ADDRESS);
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [notes, setNotes] = useState("");
  const [requestPortal, setRequestPortal] = useState(false);
  const [shippingOption, setShippingOption] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [validationFields, setValidationFields] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [fedexRatesByCode, setFedexRatesByCode] = useState({});
  const [fedexRatesStatus, setFedexRatesStatus] = useState("idle");
  const [fedexRatesError, setFedexRatesError] = useState("");
  const [fedexRatesRetryKey, setFedexRatesRetryKey] = useState(0);
  const [submission, setSubmission] = useState(null);
  const [accessToken, setAccessToken] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const productRailRef = useRef(null);
  const fedexRatesRequestIdRef = useRef(0);
  const fedexRatesLatestKeyRef = useRef("");

  useEffect(() => {
    let active = true;

    const loadCatalog = async () => {
      try {
        const response = await new PublicAPIService().getOpenOrderCatalog();
        if (active) {
          setCatalog(Array.isArray(response?.data?.groups) ? response.data.groups : []);
          setTurnstileSiteKey(String(response?.data?.turnstileSiteKey ?? ""));
        }
      } catch (error) {
        if (active) {
          setCatalogError(
            error?.response?.data?.message || "Open Order is currently unavailable.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCatalog();
    return () => {
      active = false;
    };
  }, []);

  const filteredCatalog = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return catalog;
    return catalog.filter((group) =>
      [group.productGroupName, group.manufacturerName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [catalog, search]);

  const selectedGroup = useMemo(
    () => catalog.find((group) => group.productGroupId === selectedGroupId) ?? null,
    [catalog, selectedGroupId],
  );

  const selectedLines = useMemo(
    () =>
      (selectedGroup?.products ?? [])
        .map((product) => ({
          ...product,
          quantity: Number(quantities[product.productId] ?? 0),
        }))
        .filter((product) => product.quantity > 0),
    [quantities, selectedGroup],
  );

  const subtotal = calculateEstimatedSubtotal(selectedLines);
  const selectedQuantity = selectedLines.reduce((total, line) => total + line.quantity, 0);
  const isFedexShipping = selectedGroup?.shippingMode === "fedex";
  const hasFedexRateAddress = hasValidFedexRateAddress(shippingAddress);
  const clearFedexRates = ({ clearSelection = false } = {}) => {
    fedexRatesLatestKeyRef.current = "";
    setFedexRatesByCode({});
    setFedexRatesStatus(isFedexShipping ? "incomplete" : "idle");
    setFedexRatesError("");
    if (clearSelection) setShippingOption("");
  };
  const fedexRatesPayload = useMemo(() => {
    if (!isFedexShipping || !selectedGroup?.productGroupId || !selectedLines.length || !hasFedexRateAddress) {
      return null;
    }
    return {
      productGroupId: selectedGroup.productGroupId,
      products: selectedLines.map((line) => ({
        productId: line.productId,
        quantity: Number(line.quantity ?? 0),
      })),
      shippingAddress: {
        address1: String(shippingAddress.address1 ?? "").trim(),
        address2: String(shippingAddress.address2 ?? "").trim(),
        city: String(shippingAddress.city ?? "").trim(),
        state: String(shippingAddress.state ?? "").trim().toUpperCase(),
        zip: String(shippingAddress.zip ?? "").trim(),
        countryCode: "US",
      },
    };
  }, [hasFedexRateAddress, isFedexShipping, selectedGroup?.productGroupId, selectedLines, shippingAddress]);
  const fedexRatesRequestKey = useMemo(
    () => fedexRatesPayload ? buildFedexRatesRequestKey(fedexRatesPayload) : "",
    [fedexRatesPayload],
  );
  const shippingOptions = useMemo(() => {
    return (selectedGroup?.shippingOptions ?? []).map((option) => ({
      ...option,
      ...(isFedexShipping && fedexRatesByCode[option.code]
        ? {
            available: Boolean(fedexRatesByCode[option.code].available),
            cost: fedexRatesByCode[option.code].cost,
            currency: fedexRatesByCode[option.code].currency,
            warnings: fedexRatesByCode[option.code].warnings || [],
          }
        : {}),
      value: option.code,
    }));
  }, [fedexRatesByCode, isFedexShipping, selectedGroup]);
  const selectedShipping = shippingOptions.find((option) => option.value === shippingOption);
  const invalidFieldSet = useMemo(() => new Set(validationFields), [validationFields]);
  const selectedFedexRate = isFedexShipping ? fedexRatesByCode[shippingOption] : null;
  const shippingCost = isFedexShipping
    ? (selectedFedexRate?.available ? Number(selectedFedexRate.cost ?? 0) : 0)
    : selectedShipping?.cost ?? 0;
  const quotedPayment =
    quote?.totals?.paymentOptions?.[
      paymentMethod === "bank" ? "us_bank_account" : "card"
    ] ?? null;
  const estimatedTotal = quotedPayment?.total ?? subtotal + shippingCost;
  const canContinueShipping =
    !quoteLoading &&
    (!isFedexShipping ||
      Boolean(
        shippingOption &&
          selectedFedexRate?.available &&
          Number.isFinite(Number(selectedFedexRate.cost)),
      ));

  const invalidateQuote = () => {
    setQuote(null);
    setSubmission(null);
    setAccessToken("");
    setCheckoutError("");
    setReviewOpen(false);
    setValidationFields([]);
  };

  const chooseGroup = (group) => {
    setSelectedGroupId(group.productGroupId);
    setQuantities({});
    setShippingOption("");
    setFedexRatesByCode({});
    setFedexRatesStatus(group.shippingMode === "fedex" ? "incomplete" : "idle");
    setFedexRatesError("");
    fedexRatesLatestKeyRef.current = "";
    setReviewOpen(false);
    setQuote(null);
    setSubmission(null);
    setAccessToken("");
    setCheckoutError("");
    setActiveStep(2);
  };

  const updateQuantity = (product, amount) => {
    const productId = typeof product === "object" ? product.productId : product;
    if (!productId) return;

    invalidateQuote();
    clearFedexRates({ clearSelection: isFedexShipping });
    setQuantities((current) => {
      const currentQty = Number(current[productId] ?? 0);
      const requestedQty = Math.max(0, currentQty + amount);
      const nextQty =
        amount > 0 && isStockManaged(product)
          ? Math.min(getStockQty(product), requestedQty)
          : requestedQty;
      return {
        ...current,
        [productId]: nextQty,
      };
    });
  };

  const updateRecord = (setter) => (event) => {
    const { name, value } = event.target;
    invalidateQuote();
    setter((current) => ({ ...current, [name]: value }));
  };

  const handleSameAsShipping = (event) => {
    const checked = event.target.checked;
    invalidateQuote();
    setSameAsShipping(checked);
    if (checked) setBillingAddress(shippingAddress);
  };

  const updateShipping = (event) => {
    const { name, value } = event.target;
    invalidateQuote();
    clearFedexRates({ clearSelection: isFedexShipping });
    setShippingAddress((current) => {
      const next = { ...current, [name]: value };
      if (sameAsShipping) setBillingAddress(next);
      return next;
    });
  };

  useEffect(() => {
    if (!isFedexShipping) {
      setFedexRatesStatus("idle");
      setFedexRatesByCode({});
      setFedexRatesError("");
      fedexRatesLatestKeyRef.current = "";
      return undefined;
    }

    if (!fedexRatesRequestKey) {
      setFedexRatesStatus("incomplete");
      setFedexRatesByCode({});
      setFedexRatesError("");
      fedexRatesLatestKeyRef.current = "";
      return undefined;
    }

    const currentRequestId = fedexRatesRequestIdRef.current + 1;
    fedexRatesRequestIdRef.current = currentRequestId;
    fedexRatesLatestKeyRef.current = fedexRatesRequestKey;
    setFedexRatesStatus("loading");
    setFedexRatesError("");

    const timer = window.setTimeout(async () => {
      try {
        const response = await new PublicAPIService().getOpenOrderFedexRates(fedexRatesPayload);
        if (
          fedexRatesRequestIdRef.current !== currentRequestId ||
          fedexRatesLatestKeyRef.current !== fedexRatesRequestKey
        ) {
          return;
        }

        const nextRates = mapFedexRatesByCode(response?.data?.rates || []);
        setFedexRatesByCode(nextRates);
        setFedexRatesStatus("success");
        setFedexRatesError("");
        setShippingOption((current) => current && !nextRates[current]?.available ? "" : current);
      } catch (error) {
        if (
          fedexRatesRequestIdRef.current !== currentRequestId ||
          fedexRatesLatestKeyRef.current !== fedexRatesRequestKey
        ) {
          return;
        }
        setFedexRatesByCode({});
        setFedexRatesStatus("error");
        setFedexRatesError(
          error?.response?.data?.message ||
            "Unable to calculate FedEx rates right now. Try again.",
        );
      }
    }, FEDEX_RATE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [fedexRatesPayload, fedexRatesRequestKey, fedexRatesRetryKey, isFedexShipping]);

  const validateReviewFields = () => {
    const fields = [];
    const requireText = (value, field) => {
      if (!String(value ?? "").trim()) fields.push(field);
    };
    const requirePattern = (value, field, pattern) => {
      const normalized = String(value ?? "").trim();
      if (!normalized || !pattern.test(normalized)) fields.push(field);
    };

    requireText(contact.firstName, "contact.firstName");
    requireText(contact.lastName, "contact.lastName");
    requireText(contact.businessName, "contact.businessName");
    requirePattern(contact.phone, "contact.phone", PHONE_PATTERN);
    requirePattern(contact.email, "contact.email", EMAIL_PATTERN);
    requireText(shippingAddress.address1, "shippingAddress.address1");
    requireText(shippingAddress.city, "shippingAddress.city");
    requirePattern(shippingAddress.state, "shippingAddress.state", STATE_PATTERN);
    requirePattern(shippingAddress.zip, "shippingAddress.zip", ZIP_PATTERN);
    if (!sameAsShipping) {
      requireText(billingAddress.address1, "billingAddress.address1");
      requireText(billingAddress.city, "billingAddress.city");
      requirePattern(billingAddress.state, "billingAddress.state", STATE_PATTERN);
      requirePattern(billingAddress.zip, "billingAddress.zip", ZIP_PATTERN);
    }
    if (!shippingOption) fields.push("shipping.optionCode");
    if (!selectedLines.length) fields.push("products");
    if (selectedLines.some(hasSelectedMoreThanStock)) fields.push("products");

    return Array.from(new Set(fields));
  };

  const openReview = async () => {
    const nextValidationFields = validateReviewFields();
    if (nextValidationFields.length > 0) {
      setValidationFields(nextValidationFields);
      setFormError(formatValidationMessage(nextValidationFields));
      return;
    }
    if (
      isFedexShipping &&
      (!selectedFedexRate?.available || !Number.isFinite(Number(selectedFedexRate.cost)))
    ) {
      setValidationFields(["shipping.optionCode"]);
      setFormError("Select a FedEx service with a calculated live rate before review.");
      return;
    }
    if (!turnstileToken) {
      setValidationFields(["turnstileToken"]);
      setFormError("Complete the security verification before review.");
      return;
    }

    setFormError("");
    setValidationFields([]);
    setQuoteLoading(true);
    try {
      const response = await new PublicAPIService().createOpenOrderQuote({
        contact,
        shippingAddress: { ...shippingAddress, countryCode: "US" },
        billingAddress: {
          ...(sameAsShipping ? shippingAddress : billingAddress),
          countryCode: "US",
        },
        billingSameAsShipping: sameAsShipping,
        products: selectedLines.map((line) => ({
          productId: line.productId,
          quantity: line.quantity,
        })),
        shipping: {
          mode: selectedGroup.shippingMode,
          optionCode: shippingOption,
        },
        paymentMethod: paymentMethod === "bank" ? "us_bank_account" : "card",
        notes,
        requestedPortalAccess: requestPortal,
        turnstileToken,
        honeypot,
      });
      setQuote(response?.data ?? null);
      setSubmission(null);
      setCheckoutError("");
      setReviewOpen(true);
    } catch (error) {
      setQuote(null);
      const responseFields = Array.isArray(error?.response?.data?.fields)
        ? error.response.data.fields
        : [];
      const stockShortages = error?.response?.data?.shortages;
      setValidationFields(responseFields);
      setFormError(
        error?.response?.data?.code === "INSUFFICIENT_STOCK"
          ? formatStockShortageMessage(stockShortages)
          : responseFields.length
          ? formatValidationMessage(responseFields)
          : error?.response?.data?.message ||
              "Unable to create a secure quote. Review the information and try again.",
      );
    } finally {
      setTurnstileToken("");
      setTurnstileResetKey((current) => current + 1);
      setQuoteLoading(false);
    }
  };

  const submitOrder = async () => {
    if (!quote?.quoteToken) return;
    setCheckoutLoading(true);
    setCheckoutError("");
    try {
      const api = new PublicAPIService();
      const payload = {
        quoteToken: quote.quoteToken,
        paymentMethod: paymentMethod === "bank" ? "us_bank_account" : "card",
      };
      const response = submission
        ? await api.retryOpenOrderCheckout(accessToken, {
            paymentMethod: payload.paymentMethod,
          })
        : await api.submitOpenOrder(payload);
      const nextSubmission = response?.data ?? null;
      const nextAccessToken = storeOpenOrderAccessToken(
        nextSubmission?.accessToken || accessToken,
      );
      if (nextAccessToken) setAccessToken(nextAccessToken);
      setSubmission((current) => ({ ...current, ...nextSubmission }));
      if (nextSubmission?.checkout?.checkoutUrl) {
        window.location.assign(nextSubmission.checkout.checkoutUrl);
        return;
      }
      setCheckoutError(
        nextSubmission?.checkout?.message ||
          "Your order was created, but checkout could not be started. Try again.",
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Unable to submit this Open Order. Review the quote and try again.";
      if (["QUOTE_CHANGED", "QUOTE_EXPIRED"].includes(error?.response?.data?.code)) {
        invalidateQuote();
        setFormError(message);
      } else {
        setCheckoutError(message);
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const scrollProducts = (direction) => {
    productRailRef.current?.scrollBy({ left: direction * 340, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <PublicHeader />
        <main className={styles.statePanel} aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          <h1>Loading Open Order</h1>
          <p>Retrieving the available product catalog.</p>
        </main>
      </div>
    );
  }

  if (catalogError || catalog.length === 0) {
    return (
      <div className={styles.page}>
        <PublicHeader />
        <main className={styles.statePanel}>
          <p className={styles.eyebrow}>Open Order</p>
          <h1>Ordering is currently unavailable</h1>
          <p>{catalogError || "There are no products available for Open Order at this time."}</p>
          <p className={styles.supportCopy}>
            For assistance, contact{" "}
            <a href="mailto:orders@advancedsolution.health">orders@advancedsolution.health</a>.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PublicHeader />
      <main className={styles.main}>
        <div className={styles.titleRow}>
          <h1>Place a New Order</h1>
        </div>

        <div className={styles.steps}>
          <Step
            active={activeStep === 1}
            completed={Boolean(selectedGroup)}
            headerAction={
              <label className={styles.search}>
                <span aria-hidden="true">⌕</span>
                <input
                  aria-label="Search product groups"
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search"
                  type="search"
                  value={search}
                />
              </label>
            }
            number="1"
            onOpen={() => setActiveStep(1)}
            summary={selectedGroup?.productGroupName}
            summaryContent={
              selectedGroup ? (
                <div className={styles.productStepSummary}>
                  <strong>{selectedGroup.productGroupName}</strong>
                  <span>{selectedGroup.manufacturerName || "Advanced Solution"}</span>
                </div>
              ) : null
            }
            title="Choose Product"
          >
            <div className={styles.catalogRailWrap}>
              <div className={styles.catalogRail} ref={productRailRef}>
                {filteredCatalog.map((group) => (
                  <button
                    aria-pressed={selectedGroupId === group.productGroupId}
                    className={`${styles.productCard} ${
                      selectedGroupId === group.productGroupId ? styles.productCardSelected : ""
                    }`}
                    key={group.productGroupId}
                    onClick={() => chooseGroup(group)}
                    type="button"
                  >
                    {group.imageUrl ? (
                      <img
                        alt=""
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                        referrerPolicy="no-referrer"
                        src={group.imageUrl}
                      />
                    ) : null}
                    <strong>{group.productGroupName}</strong>
                    <span>{group.manufacturerName || "Advanced Solution"}</span>
                  </button>
                ))}
                {filteredCatalog.length === 0 && (
                  <p className={styles.emptyRail}>No product groups match your search.</p>
                )}
              </div>
            </div>
            <div className={styles.railActions}>
              <button
                aria-label="Scroll product groups left"
                className={styles.railButton}
                onClick={() => scrollProducts(-1)}
                type="button"
              >
                ←
              </button>
              <button
                aria-label="Scroll product groups right"
                className={styles.railButton}
                onClick={() => scrollProducts(1)}
                type="button"
              >
                →
              </button>
            </div>
          </Step>

          <Step
            active={activeStep === 2}
            completed={selectedLines.length > 0}
            number="2"
            onOpen={() => selectedGroup && setActiveStep(2)}
            summary={
              selectedLines.length > 0
                ? `${selectedQuantity} item(s) selected`
                : ""
            }
            summaryContent={
              selectedLines.length > 0 ? (
                <div className={styles.sizeStepSummary}>
                  <strong>{formatCurrency(subtotal)}</strong>
                  <span>{String(selectedQuantity).padStart(2, "0")}</span>
                </div>
              ) : null
            }
            title="Select Sizes and Quantity"
          >
            {!selectedGroup ? (
              <p>Choose a product group first.</p>
            ) : (
              <>
                <div className={styles.productRows}>
                  {selectedGroup.products.map((product) => {
                    const quantity = Number(quantities[product.productId] ?? 0);
                    const stockMessage = getStockStatusMessage(product);
                    const isOutOfStock = isStockManaged(product) && getStockQty(product) === 0;
                    const hasMaxStock = isStockManaged(product) && quantity >= getStockQty(product);
                    return (
                      <article className={styles.productRow} key={product.productId}>
                        <div className={styles.sizeTile}>
                          <strong>{product.productCode || "—"}</strong>
                        </div>
                        <div className={styles.productDetails}>
                          <span>{product.productName || product.productCode || "Product"}</span>
                          <strong>{formatCurrency(product.standardPrice)} each</strong>
                          {stockMessage ? (
                            <small className={styles.stockAlert}>{stockMessage}</small>
                          ) : null}
                        </div>
                        <strong className={styles.linePrice}>
                          {formatCurrency(product.standardPrice * quantity)}
                        </strong>
                        <div className={styles.quantityControl}>
                          <button
                            aria-label={`Decrease ${product.productName} quantity`}
                            disabled={quantity === 0}
                            onClick={() => updateQuantity(product, -1)}
                            type="button"
                          >
                            −
                          </button>
                          <output aria-label={`${product.productName} quantity`}>{quantity}</output>
                          <button
                            aria-label={`Increase ${product.productName} quantity`}
                            disabled={isOutOfStock || hasMaxStock}
                            onClick={() => updateQuantity(product, 1)}
                            type="button"
                          >
                            +
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
                <div className={styles.stepActions}>
                  <span>
                    Estimated subtotal <strong>{formatCurrency(subtotal)}</strong>
                  </span>
                  <button
                    aria-label="Continue"
                    className={styles.primaryButton}
                    disabled={selectedLines.length === 0}
                    onClick={() => setActiveStep(3)}
                    type="button"
                  >
                    Continue ↓
                  </button>
                </div>
              </>
            )}
          </Step>

          <Step
            active={activeStep === 3}
            completed={Boolean(quote)}
            number="3"
            onOpen={() => selectedLines.length > 0 && setActiveStep(3)}
            summary={contact.businessName}
            summaryContent={
              contact.businessName ? (
                <div className={styles.shippingStepSummary}>
                  <strong>{contact.businessName}</strong>
                  <span>
                    {[shippingAddress.city, shippingAddress.state].filter(Boolean).join(", ")}
                  </span>
                </div>
              ) : null
            }
            title="Enter Shipping Details"
          >
            <div className={styles.shippingLayout}>
              <div className={styles.shippingForms}>
                <section className={styles.contactFields}>
                  <InputField className={styles.fieldStandard} invalid={invalidFieldSet.has("contact.firstName")} label="First Name" name="firstName" onChange={updateRecord(setContact)} placeholder="Enter First Name" required value={contact.firstName} />
                  <InputField className={styles.fieldStandard} invalid={invalidFieldSet.has("contact.lastName")} label="Last Name" name="lastName" onChange={updateRecord(setContact)} placeholder="Enter Last Name" required value={contact.lastName} />
                  <InputField className={styles.fieldStandard} invalid={invalidFieldSet.has("contact.businessName")} label="Clinic / Business Name" name="businessName" onChange={updateRecord(setContact)} placeholder="Enter Clinic / Business Name" required value={contact.businessName} />
                  <InputField ariaLabel="Phone Number" className={styles.fieldStandard} invalid={invalidFieldSet.has("contact.phone")} label="Phone" name="phone" onChange={updateRecord(setContact)} placeholder="Enter Phone" required type="tel" value={contact.phone} />
                  <InputField ariaLabel="Email Address" className={styles.fieldStandard} invalid={invalidFieldSet.has("contact.email")} label="Email" name="email" onChange={updateRecord(setContact)} placeholder="Enter Email" required type="email" value={contact.email} />
                </section>

                <section className={styles.addressSection}>
                  <h2>Shipping Address</h2>
                  <AddressFields invalidFields={invalidFieldSet} onChange={updateShipping} validationPrefix="shippingAddress" value={shippingAddress} />
                </section>

                <section className={styles.addressSection}>
                  <div className={styles.sectionHeadingWithCheck}>
                    <h2>Billing Address</h2>
                    <label className={styles.checkbox}>
                      <input checked={sameAsShipping} onChange={handleSameAsShipping} type="checkbox" />
                      Same as Shipping Address
                    </label>
                  </div>
                  <AddressFields
                    disabled={sameAsShipping}
                    invalidFields={invalidFieldSet}
                    onChange={updateRecord(setBillingAddress)}
                    validationPrefix="billingAddress"
                    value={sameAsShipping ? shippingAddress : billingAddress}
                  />
                </section>

                <section className={styles.notesSection}>
                  <label className={`${styles.field} ${styles.notesField}`}>
                    <span className={styles.fieldLabel}>
                      <span>Enter Notes</span>
                      <span className={styles.optionalText}> (optional)</span>
                    </span>
                    <textarea
                      maxLength={1000}
                      onChange={(event) => {
                        invalidateQuote();
                        setNotes(event.target.value);
                      }}
                      placeholder="Enter any notes for this order"
                      rows="4"
                      value={notes}
                    />
                  </label>
                </section>

                <section className={styles.portalPanel}>
                  <div className={styles.portalLogo}>
                    <img className={styles.portalIcon} src="/tissue_vault_icon.svg" alt="" aria-hidden="true" />
                    <img className={styles.portalLogoText} src="/TissueVaultTextLogo.svg" alt="Tissue Vault" />
                    <span aria-hidden="true" className={styles.registeredMark}>®</span>
                  </div>
                  <p>
                    Tissue Vault® is our proprietary client portal that allows you to easily place and manage orders,
                    invoices, IVRs, and more. Please let us know if you are interested, and we’ll get in touch with
                    you to get started.
                  </p>
                  <label className={styles.checkbox}>
                    <input checked={requestPortal} onChange={(event) => {
                      invalidateQuote();
                      setRequestPortal(event.target.checked);
                    }} type="checkbox" />
                    Request a Tissue Vault® account
                  </label>
                </section>
              </div>

              <aside className={styles.shippingPreference}>
                <h2>Choose Shipping Preference</h2>
                <div className={styles.shippingOptions}>
                  {shippingOptions.map((option) => (
                    <label
                      className={`${styles.shippingOption} ${
                        shippingOption === option.value ? styles.shippingOptionSelected : ""
                      } ${
                        invalidFieldSet.has("shipping.optionCode") ? styles.shippingOptionInvalid : ""
                      } ${
                        isFedexShipping && option.available === false ? styles.shippingOptionDisabled : ""
                      }`}
                      key={option.value}
                    >
                      <input
                        checked={shippingOption === option.value}
                        name="shippingOption"
                        onChange={() => {
                          invalidateQuote();
                          if (isFedexShipping && option.available === false) return;
                          setShippingOption(option.value);
                        }}
                        disabled={isFedexShipping && option.available === false}
                        type="radio"
                      />
                      <span>
                        <strong>{option.label}</strong>
                        {option.detail && <small>{option.detail}</small>}
                      </span>
                      <strong>
                        {isFedexShipping && fedexRatesStatus === "loading"
                          ? "Calculating…"
                          : isFedexShipping && option.available === false
                            ? "Unavailable"
                            : option.cost === null
                              ? "Quote"
                              : formatCurrency(option.cost)}
                      </strong>
                    </label>
                  ))}
                </div>
                {isFedexShipping && fedexRatesStatus === "error" ? (
                  <div className={styles.rateError} role="alert">
                    <span>{fedexRatesError}</span>
                    <button
                      onClick={() => setFedexRatesRetryKey((current) => current + 1)}
                      type="button"
                    >
                      Retry
                    </button>
                  </div>
                ) : null}
                <div className={styles.turnstilePanel}>
                  <span>Security verification</span>
                  {turnstileSiteKey ? (
                    <TurnstileWidget
                      onToken={setTurnstileToken}
                      resetKey={turnstileResetKey}
                      siteKey={turnstileSiteKey}
                    />
                  ) : (
                    <small>Security verification is unavailable. Please try again later.</small>
                  )}
                </div>
                <label className={styles.honeypot} aria-hidden="true">
                  Website
                  <input
                    autoComplete="off"
                    onChange={(event) => setHoneypot(event.target.value)}
                    tabIndex="-1"
                    value={honeypot}
                  />
                </label>
                {formError && <p className={styles.formError} role="alert">{formError}</p>}
              </aside>
            </div>
            <div className={styles.shippingStepFooter}>
              <button
                aria-label="Continue to Review"
                className={styles.primaryButton}
                disabled={!canContinueShipping}
                onClick={openReview}
                type="button"
              >
                {quoteLoading ? "Creating Secure Quote…" : "Continue ↓"}
              </button>
            </div>
          </Step>
        </div>
        <div className={styles.reviewActionBar}>
          <button
            className={styles.reviewButton}
            disabled={!quote || selectedLines.length === 0}
            onClick={() => setReviewOpen(true)}
            type="button"
          >
            Review Order
          </button>
        </div>
      </main>

      {reviewOpen && (
        <div className={styles.reviewOverlay} role="presentation">
          <button aria-label="Close order review" className={styles.reviewBackdrop} onClick={() => setReviewOpen(false)} type="button" />
          <aside aria-label="Order summary" className={styles.reviewDrawer}>
            <div className={styles.reviewHeader}>
              <h2>Order Summary</h2>
              <button aria-label="Close order review" onClick={() => setReviewOpen(false)} type="button">×</button>
            </div>
            <div className={styles.reviewBody}>
              <section className={styles.reviewSection}>
                <h3>Products</h3>
                {(quote?.lineItems ?? selectedLines).map((line) => (
                  <div className={styles.reviewLine} key={line.productId}>
                    <div>
                      <strong>{line.productName}</strong>
                      <span>{line.productCode} · Qty {line.quantity}</span>
                    </div>
                    <strong>{formatCurrency(line.lineTotal ?? line.standardPrice * line.quantity)}</strong>
                  </div>
                ))}
              </section>
              <section className={styles.reviewSection}>
                <h3>Ship To</h3>
                <p>
                  <strong>{contact.businessName || "Shipping details pending"}</strong><br />
                  {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}<br />
                  {shippingAddress.address1}<br />
                  {[shippingAddress.city, shippingAddress.state, shippingAddress.zip].filter(Boolean).join(", ")}
                </p>
              </section>
              <section className={styles.totalSection}>
                <div><span>Subtotal</span><strong>{formatCurrency(quote?.totals?.productsSubtotal ?? subtotal)}</strong></div>
                <div>
                  <span>Shipping</span>
                  <strong>{formatCurrency(quote?.totals?.shipping ?? shippingCost)}</strong>
                </div>
                <div><span>Processing fee</span><strong>{formatCurrency(quotedPayment?.processingFee ?? quote?.totals?.processingFee ?? 0)}</strong></div>
                <div className={styles.grandTotal}><span>Estimated total</span><strong>{formatCurrency(estimatedTotal)}</strong></div>
              </section>
              <section>
                <h3>Payment Method</h3>
                <div className={styles.paymentTabs}>
                  <button className={paymentMethod === "card" ? styles.paymentActive : ""} onClick={() => setPaymentMethod("card")} type="button">Card</button>
                  <button className={paymentMethod === "bank" ? styles.paymentActive : ""} onClick={() => setPaymentMethod("bank")} type="button">Bank Transfer</button>
                </div>
              </section>
              {quote?.pricingUpdated && (
                <div className={styles.pricingUpdated}>
                  Final account pricing was applied to this secure quote.
                </div>
              )}
              <div className={styles.secureNotice}>
                {submission?.orderReference ? (
                  <>Order {submission.orderReference} was created and is awaiting payment.</>
                ) : (
                  <>
                    Secure quote created and valid until{" "}
                    {quote?.expiresAt ? new Date(quote.expiresAt).toLocaleTimeString() : "the displayed expiration time"}.
                  </>
                )}
              </div>
              {checkoutError && <p className={styles.formError} role="alert">{checkoutError}</p>}
              <button
                className={styles.checkoutButton}
                disabled={checkoutLoading}
                onClick={submitOrder}
                type="button"
              >
                {checkoutLoading
                  ? "Starting Secure Checkout…"
                  : submission?.checkout?.status === "failed"
                    ? "Retry Secure Checkout"
                    : "Continue to Secure Checkout"}
              </button>
              {submission?.orderReference && accessToken && (
                <button
                  className={styles.textButton}
                  onClick={() =>
                    window.location.assign(
                      submission.statusPath || buildOpenOrderStatusPath(accessToken),
                    )
                  }
                  type="button"
                >
                  View order status
                </button>
              )}
              <p className={styles.checkoutCaption}>You will be redirected to a secure payment page.</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default OpenOrderForm;
